import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  attachLesson,
  createLesson,
  detachLesson,
  getModule,
  listLessons,
  type LessonListItem,
  type ModuleDetail,
} from "../api/courses";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";

function canEditContent(roles: string[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "admin" || r === "content_manager");
}

export function ModuleDetailPage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const id = Number(moduleId);
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [mod, setMod] = useState<ModuleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New lesson inline form
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newSlugTouched, setNewSlugTouched] = useState(false);
  const [newMinutes, setNewMinutes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Attach existing lesson dialog
  const [attachOpen, setAttachOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [searchResults, setSearchResults] = useState<LessonListItem[]>([]);

  const reload = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError("Некорректный модуль");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const m = await getModule(id);
      setMod(m);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setError("Модуль не найден");
      else setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ), 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  useEffect(() => {
    if (!attachOpen) return;
    void listLessons({ q: debouncedQ || undefined, page_size: 20 }).then((r) =>
      setSearchResults(r.items),
    );
  }, [attachOpen, debouncedQ]);

  if (!user) return null;
  const canEdit = canEditContent(user.roles);

  function translit(value: string): string {
    return value
      .toLowerCase()
      .replace(/ё/g, "yo")
      .replace(/[а-я]/g, (c) => {
        const map: Record<string, string> = {
          а: "a", б: "b", в: "v", г: "g", д: "d", е: "e",
          ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
          н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u",
          ф: "f", х: "h", ц: "c", ч: "ch", ш: "sh", щ: "sch",
          ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
        };
        return map[c] ?? c;
      })
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function handleTitleChange(v: string) {
    setNewTitle(v);
    if (!newSlugTouched) setNewSlug(translit(v));
  }

  async function handleCreateLesson(e: FormEvent) {
    e.preventDefault();
    if (!mod) return;
    setSubmitting(true);
    try {
      const lesson = await createLesson({
        title: newTitle.trim(),
        slug: newSlug.trim(),
        estimated_minutes: newMinutes ? Number(newMinutes) : undefined,
        content_markdown: "",
      });
      await attachLesson(mod.id, lesson.id);
      // Reset form
      setNewTitle("");
      setNewSlug("");
      setNewSlugTouched(false);
      setNewMinutes("");
      setNewOpen(false);
      // Navigate straight into the lesson editor — that's where authoring happens
      navigate(`/lessons/${lesson.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === "SLUG_TAKEN") {
        alert("Урок с таким slug уже существует. Поменяй slug.");
      } else {
        alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAttach(lessonId: number) {
    if (!mod) return;
    try {
      await attachLesson(mod.id, lessonId);
      setAttachOpen(false);
      setSearchQ("");
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.code === "LESSON_ALREADY_IN_MODULE") {
        alert("Этот урок уже в модуле");
      } else {
        alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
      }
    }
  }

  async function handleDetach(lessonId: number, title: string) {
    if (!mod) return;
    if (!confirm(`Отсоединить «${title}» от модуля? Урок останется в библиотеке.`)) return;
    try {
      await detachLesson(mod.id, lessonId);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  if (loading && !mod) {
    return <div className="mx-auto max-w-5xl px-4 py-12 text-center text-slate-400">Загрузка…</div>;
  }

  if (error || !mod) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-12 text-center">
        <p className="text-slate-500">{error ?? "Модуль не найден"}</p>
        <Link to="/courses" className="mt-4 inline-block text-sm text-brand-accent hover:underline">
          ← к курсам
        </Link>
      </div>
    );
  }

  const attachedIds = new Set(mod.lessons.map((l) => l.lesson_id));

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6">
        <Link to="/courses" className="text-sm text-slate-500 hover:text-brand-accent">
          ← к курсам
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">{mod.title}</h1>
        {mod.description && (
          <p className="mt-1 max-w-2xl text-sm text-slate-700">{mod.description}</p>
        )}
      </header>

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900">Уроки</h2>
          {canEdit && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAttachOpen(true)}
                className="btn-secondary"
              >
                Из библиотеки
              </button>
              <button
                type="button"
                onClick={() => setNewOpen((s) => !s)}
                className="btn-primary"
              >
                {newOpen ? "Отмена" : "+ Новый урок"}
              </button>
            </div>
          )}
        </div>

        {newOpen && (
          <form
            onSubmit={handleCreateLesson}
            className="mb-3 space-y-2 rounded-lg border border-slate-200 bg-white p-4"
          >
            <input
              type="text"
              required
              placeholder="Название урока"
              value={newTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              autoFocus
            />
            <div className="flex gap-2">
              <input
                type="text"
                required
                pattern="[a-z0-9\-]+"
                placeholder="slug"
                value={newSlug}
                onChange={(e) => {
                  setNewSlug(e.target.value);
                  setNewSlugTouched(true);
                }}
                className="flex-1 rounded border border-slate-300 px-3 py-2 font-mono text-sm"
              />
              <input
                type="number"
                min={1}
                max={600}
                placeholder="мин."
                value={newMinutes}
                onChange={(e) => setNewMinutes(e.target.value)}
                className="w-24 rounded border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                После создания откроется редактор содержимого.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary"
              >
                {submitting ? "…" : "Создать и открыть"}
              </button>
            </div>
          </form>
        )}

        {mod.lessons.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            В модуле пока нет уроков.
          </div>
        ) : (
          <ol className="space-y-2">
            {mod.lessons.map((l, idx) => (
              <li
                key={l.lesson_id}
                className="flex items-center justify-between rounded-lg bg-white px-4 py-3 shadow-sm"
              >
                <Link to={`/lessons/${l.lesson_id}`} className="min-w-0 flex-1 group">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{idx + 1}.</span>
                    <span className="font-medium text-slate-900 group-hover:text-brand-accent">
                      {l.title}
                    </span>
                    <span className="badge badge-neutral">
                      {l.lesson_type}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500">
                    <span className="font-mono">{l.slug}</span>
                    {l.estimated_minutes && <span>{l.estimated_minutes} мин</span>}
                  </div>
                </Link>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleDetach(l.lesson_id, l.title)}
                    className="ml-3 text-xs text-slate-500 hover:text-red-600 hover:underline"
                  >
                    Отсоединить
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      {attachOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-lg space-y-3 rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-start justify-between">
              <h3 className="text-base font-semibold text-slate-900">Из библиотеки</h3>
              <button
                type="button"
                onClick={() => setAttachOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            </div>
            <input
              type="search"
              placeholder="Поиск по названию или slug…"
              autoFocus
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
            <div className="max-h-80 space-y-1 overflow-y-auto">
              {searchResults.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">Ничего не найдено</p>
              ) : (
                searchResults.map((l) => {
                  const already = attachedIds.has(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      disabled={already}
                      onClick={() => handleAttach(l.id)}
                      className="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      <div>
                        <div className="text-slate-900">{l.title}</div>
                        <div className="font-mono text-xs text-slate-500">{l.slug}</div>
                      </div>
                      {already && <span className="text-xs text-slate-400">Уже в модуле</span>}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
